import { useEffect, useRef, useState } from "react";
import BaseModal from "./BaseModal";
import Icon1 from "@assets/icons/Icon";
import Button from "@components/ui/buttons/Button";
import { projectsApi } from "@services/api/projects";
import { useDataStore } from "@store/user_org";
import { showNotification } from "context/notification.service";
import type { BillingComment } from "types";

interface Props {
  projectId: string;
  projectName?: string;
  initialComments: BillingComment[];
  onClose: () => void;
  /** Notifies the caller of the new comment list so it can keep its
   * cached project up to date and refresh the row's badge count. */
  onCommentsChange?: (comments: BillingComment[]) => void;
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    const today = new Date();
    const sameDay =
      d.getFullYear() === today.getFullYear() &&
      d.getMonth() === today.getMonth() &&
      d.getDate() === today.getDate();
    const time = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    if (sameDay) return `Aujourd'hui à ${time}`;
    return `${d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })} à ${time}`;
  } catch {
    return iso;
  }
}

export default function BillingCommentsModal({
  projectId,
  projectName,
  initialComments,
  onClose,
  onCommentsChange,
}: Props) {
  const userData = useDataStore((s) => s.userData);
  const [comments, setComments] = useState<BillingComment[]>(initialComments || []);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  };

  useEffect(() => {
    // Mark as seen on open — fire-and-forget. The optimistic badge zero-out
    // happens upstream when the modal is opened.
    projectsApi.markSeen(projectId, "billing_comments").catch((e) => {
      console.warn("mark-seen(billing_comments) failed", e);
    });
    scrollToBottom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed || posting) return;
    setPosting(true);
    try {
      const created = await projectsApi.addBillingComment(projectId, trimmed);
      const next = [...comments, created];
      setComments(next);
      onCommentsChange?.(next);
      setText("");
      scrollToBottom();
    } catch (e: any) {
      console.error("Failed to post billing comment", e);
      const detail = e?.response?.data?.detail || "Échec de l'envoi";
      showNotification({ type: "error", message: detail });
    } finally {
      setPosting(false);
    }
  };

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <BaseModal
      onClose={onClose}
      title={projectName ? `Commentaires — ${projectName}` : "Commentaires facturation"}
      startIcon={<Icon1 name="message" size={20} />}
      width="max-w-xl"
    >
      <div className="flex flex-col" style={{ height: "70vh" }}>
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 bg-neutral-50">
          {comments.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center text-neutral-400">
              <Icon1 name="message" size={48} color="#d4d4d4" />
              <div className="text-h3 mt-3">Aucun commentaire pour l'instant</div>
              <div className="text-h4 mt-1">Démarrez la conversation ci-dessous.</div>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {comments.map((c) => {
                const mine = c.author_id === userData?.id;
                return (
                  <div
                    key={c.id}
                    className={`flex flex-col gap-1 ${mine ? "items-end" : "items-start"}`}
                  >
                    <div
                      className={`max-w-[80%] px-3 py-2 rounded-2xl text-h3 whitespace-pre-wrap break-words ${
                        mine
                          ? "bg-blue-500 text-white rounded-br-sm"
                          : "bg-white border border-neutral-200 text-neutral-800 rounded-bl-sm"
                      }`}
                    >
                      {c.text}
                    </div>
                    <div className="text-h5 text-neutral-400 px-2">
                      {mine ? "Vous" : c.author_name} · {formatTimestamp(c.created_at)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="border-t border-neutral-200 p-3 bg-white flex flex-row items-end gap-2">
          <textarea
            className="flex-1 resize-none border border-neutral-300 rounded-lg p-2 text-h3 focus:outline-none focus:border-blue-500 min-h-10 max-h-32"
            rows={2}
            placeholder="Écrire un commentaire..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKey}
            disabled={posting}
          />
          <Button
            label={posting ? "Envoi..." : "Envoyer"}
            onClick={handleSend}
            disabled={posting || !text.trim()}
          />
        </div>
      </div>
    </BaseModal>
  );
}
